import { useState } from "react";
import { Button, Col, Container, Modal, Row } from "react-bootstrap";
import { BACK_CARD_VARIANTS } from "../../back-card-variants";
import { INITIAL_SCENARIO, ScenarioData } from "./mock-data";
import ScenarioEditor from "./ScenarioEditor";

/**
 * Every variant is rendered twice (basic and pro) from the same shared
 * scenario object. The fixed-position editor lets you mutate any field live
 * and watch all cards update.
 *
 * "Replay flip" remounts every card so the flip-in animation re-fires.
 * "Recalc values" shifts the numeric values so ChangeHighlight pulses on
 * the wrapped Exp. Return / Odds fields.
 */
const UiLibraryContestPage = () => {
  const [scenario, setScenario] = useState<ScenarioData>(INITIAL_SCENARIO);
  const [flipKey, setFlipKey] = useState(0);
  const [recalcTick, setRecalcTick] = useState(0);
  const [betAmounts, setBetAmounts] = useState<Record<string, string>>({});
  const [editorOpen, setEditorOpen] = useState(false);

  const handleBetAmount = (key: string, value: string) => {
    setBetAmounts((prev) => ({ ...prev, [key]: value }));
  };

  // Each recalc click shifts the numeric value by a small delta so every
  // click produces a different string and ChangeHighlight actually pulses.
  const bumpValue = (baseValue: string | undefined) => {
    if (!baseValue) return baseValue;
    if (recalcTick === 0) return baseValue;
    const match = baseValue.match(/[-+]?[0-9]*\.?[0-9]+([,][0-9]+)*/);
    if (!match) return baseValue;
    const raw = parseFloat(match[0].replace(/,/g, ""));
    if (Number.isNaN(raw)) return baseValue;
    const direction = recalcTick % 2 === 0 ? -1 : 1;
    const shifted = raw * (1 + direction * 0.05 * recalcTick);
    if (baseValue.endsWith("X")) {
      return `${shifted.toFixed(2)}X`;
    }
    if (baseValue.startsWith("§")) {
      const formatted = shifted.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
      return `§${formatted}`;
    }
    return Math.round(shifted).toString();
  };

  const MODES: { id: string; isBasic: boolean }[] = [
    { id: "basic", isBasic: true },
    { id: "pro", isBasic: false },
  ];

  return (
    <div style={{ padding: 20, minHeight: "100vh" }}>
      <Modal
        show={editorOpen}
        onHide={() => setEditorOpen(false)}
        scrollable
      >
        <Modal.Header closeButton>
          <Modal.Title>Edit scenario</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <ScenarioEditor scenario={scenario} onChange={setScenario} />
        </Modal.Body>
      </Modal>
      <Container fluid>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 20,
            position: "sticky",
            top: 0,
            zIndex: 10,
            padding: "12px 16px",
            margin: "0 -16px 20px",
            backdropFilter: "blur(12px)",
            WebkitBackdropFilter: "blur(12px)",
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            borderRadius: 8,
          }}
        >
          <div style={{ color: "var(--text-color)" }}>
            <h2 style={{ marginBottom: 0 }}>Bet Card Back Side</h2>
          </div>
          <div
            style={{
              display: "flex",
              gap: 10,
              alignItems: "center",
              color: "var(--text-color)",
            }}
          >
            <div style={{ opacity: 0.5, fontSize: 11 }}>
              flips: {flipKey} · recalcs: {recalcTick}
            </div>
            <Button
              variant="outline-info"
              onClick={() => setEditorOpen(true)}
            >
              Edit data
            </Button>
            <Button
              variant="outline-light"
              onClick={() => setFlipKey((k) => k + 1)}
            >
              Replay flip
            </Button>
            <Button
              variant="outline-warning"
              onClick={() => setRecalcTick((t) => t + 1)}
            >
              Recalc values
            </Button>
          </div>
        </div>

        {BACK_CARD_VARIANTS.map((variant) => (
          <div
            key={variant.id}
            style={{
              marginBottom: 32,
              paddingBottom: 16,
              borderBottom: "1px solid rgba(128,128,128,0.3)",
              color: "var(--text-color)",
            }}
          >
            <div style={{ marginBottom: 12 }}>
              <h4 style={{ marginBottom: 0 }}>{variant.name}</h4>
            </div>
            <Row>
              {MODES.map((mode) => {
                const key = `${variant.id}-${mode.id}-${flipKey}`;
                const betAmountKey = `${variant.id}-${mode.id}`;
                const Component = variant.Component;
                return (
                  <Col
                    key={key}
                    md={12}
                    lg={6}
                    xxl={4}
                    style={{ marginBottom: 16 }}
                  >
                    <Component
                      {...scenario}
                      isBasic={mode.isBasic}
                      expReturn={bumpValue(scenario.expReturn)}
                      oddsLabel={bumpValue(scenario.oddsLabel)}
                      balanceValue={bumpValue(scenario.balanceValue)}
                      betAmount={betAmounts[betAmountKey] ?? ""}
                      onBetAmountChange={(v) =>
                        handleBetAmount(betAmountKey, v)
                      }
                      onCancel={() => {}}
                      onConfirm={() => {}}
                    />
                  </Col>
                );
              })}
            </Row>
          </div>
        ))}
      </Container>
    </div>
  );
};

export default UiLibraryContestPage;
